import { DownOutlined } from '@ant-design/icons';
import { Affix, Breadcrumb, Checkbox, Col, Dropdown, Form, Input, InputNumber, Layout, MenuProps, Row, Select, Space, Spin, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import _ from 'lodash';
import objectHash from 'object-hash';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import ApplicationFeeFormatEnum from '~Api/Deal/ApplicationFeeFormatEnum';
import BrokerageFeeFormatEnum from '~Api/Deal/BrokerageFeeFormatEnum';
import EstablishmentFeeFormatEnum from '~Api/Deal/EstablishmentFeeFormatEnum';
import IPostcodeCategory from '~Api/Deal/IPostcodeCategory';
import MortgageTypeEnum from '~Api/Deal/MortgageTypeEnum';
import ZoneTypeEnum from '~Api/Deal/ZoneTypeEnum';
import constants from '~constants';
import { dealPropertiesPostcodeCategoriesListAction } from '~Deals/actions';
import ICalculatorProperty from '~Deals/ICalculatorProperty';
import { dealPropertiesPostcodeCategoriesSelector } from '~Deals/selectors';
import {
    IDealCalculateParameters,
    IDealCalculatedAmounts,
    getDealCalculatedAmounts,
    getDealCalculatedEstablishmentFeePercentage,
    getDealCalculatedInterestRate,
} from '~Deals/utilities';
import {
    IDealCalculateValidationErrors,
    validateDealEstablishmentFee,
    validateDealInterestRate,
    validateDealLegalFees,
    validateDealMaximumLvr,
} from '~Leads/validators';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import './scenario-calculator.less';
import { currencyFormatter } from '~utilities/formatters';
import InterestMethodEnum from '~Api/Deal/DealInterestMethodEnum';

interface IState {
    applicationFeeDollars: number;
    applicationFeeFormat: ApplicationFeeFormatEnum;
    applicationFeePercentage: number;
    brokerageFeeDollars: number;
    brokerageFeeFormat: BrokerageFeeFormatEnum;
    brokerageFeePercentage: number;
    calculatedAmounts: IDealCalculatedAmounts;
    calculateParametersHash: string;
    commitmentFee: number;
    dirtyFields: IDictionary<boolean>;
    errors: IDealCalculateValidationErrors;
    establishmentFeeDollars: number;
    establishmentFeeFormat: EstablishmentFeeFormatEnum;
    establishmentFeePercentage: number;
    estimatedOutlays: number;
    interestRate: number;
    isThirdParty: boolean;
    legalFees: number;
    maximumLvr: number;
    mortgageType: MortgageTypeEnum;
    properties: ICalculatorProperty[];
    requestedPayoutAmount: number;
    termMonths: number;
}

interface IPropsSelector {
    postcodeCategories: IDictionary<IPostcodeCategory>;
}

interface IPropsDispatch {
    postcodeCategoriesList: () => void;
}

type Props = IPropsSelector & IPropsDispatch;

class ScenarioCalculator extends React.Component<Props, IState> {
    public state: IState = {
        applicationFeeDollars: null,
        applicationFeeFormat: ApplicationFeeFormatEnum.Percentage,
        applicationFeePercentage: null,
        brokerageFeeDollars: null,
        brokerageFeeFormat: BrokerageFeeFormatEnum.Percentage,
        brokerageFeePercentage: null,
        calculateParametersHash: null,
        calculatedAmounts: null,
        commitmentFee: null,
        dirtyFields: {},
        errors: {},
        establishmentFeeDollars: null,
        establishmentFeeFormat: EstablishmentFeeFormatEnum.Percentage,
        establishmentFeePercentage: null,
        estimatedOutlays: 600,
        interestRate: null,
        isThirdParty: null,
        legalFees: constants.DEAL_LEGAL_FEES_DOLLARS_DEFAULT,
        maximumLvr: 60,
        mortgageType: MortgageTypeEnum.FirstMortgage,
        properties: [
            {
                currentDebt: null,
                estimatedValue: null,
                postcode: null,
                zoneType: null,
            },
        ],
        requestedPayoutAmount: null,
        termMonths: null,
    };

    private debouncedCalculateDealAmounts: () => void = null;

    constructor(props: Props) {
        super(props);

        this.getInterestRate = this.getInterestRate.bind(this);
        this.getCalculatedInterestRate = this.getCalculatedInterestRate.bind(this);
        this.onClickUseMatrixInterestRate = this.onClickUseMatrixInterestRate.bind(this);
        this.getEstablishmentFeePercentage = this.getEstablishmentFeePercentage.bind(this);
        this.getCalculatedEstablishmentFeePercentage = this.getCalculatedEstablishmentFeePercentage.bind(this);
        this.onClickApplicationFeeFormat = this.onClickApplicationFeeFormat.bind(this);
        this.onClickBrokerageFeeFormat = this.onClickBrokerageFeeFormat.bind(this);
        this.onClickEstablishmentFeeFormat = this.onClickEstablishmentFeeFormat.bind(this);

        this.onChangeApplicationFeePercentage = this.onChangeApplicationFeePercentage.bind(this);
        this.onChangeApplicationFeeDollars = this.onChangeApplicationFeeDollars.bind(this);
        this.onChangeBrokerageFeePercentage = this.onChangeBrokerageFeePercentage.bind(this);
        this.onChangeBrokerageFeeDollars = this.onChangeBrokerageFeeDollars.bind(this);
        this.onChangeCommitmentFee = this.onChangeCommitmentFee.bind(this);
        this.onChangeEstablishmentFeePercentage = this.onChangeEstablishmentFeePercentage.bind(this);
        this.onChangeEstablishmentFeeDollars = this.onChangeEstablishmentFeeDollars.bind(this);
        this.onChangeEstimatedOutlays = this.onChangeEstimatedOutlays.bind(this);
        this.onChangeIsThirdParty = this.onChangeIsThirdParty.bind(this);
        this.onChangeLoanAmount = this.onChangeLoanAmount.bind(this);
        this.onChangePropertyValue = this.onChangePropertyValue.bind(this);
        this.onChangePropertyCurrentDebt = this.onChangePropertyCurrentDebt.bind(this);
        this.onChangePostcode = this.onChangePostcode.bind(this);
        this.onChangeZoneType = this.onChangeZoneType.bind(this);
        this.onChangeLoanAmount = this.onChangeLoanAmount.bind(this);
        this.onChangeInterestRate = this.onChangeInterestRate.bind(this);
        this.onChangeLegalFees = this.onChangeLegalFees.bind(this);
        this.onChangeMaximumLvr = this.onChangeMaximumLvr.bind(this);

        this.validateEstablishmentFee = this.validateEstablishmentFee.bind(this);
        this.validateInterestRate = this.validateInterestRate.bind(this);
        this.validateLegalFees = this.validateLegalFees.bind(this);
        this.validateMaximumLvr = this.validateMaximumLvr.bind(this);

        this.setError = this.setError.bind(this);

        this.onChangeMortgageType = this.onChangeMortgageType.bind(this);
        this.onChangeTermMonths = this.onChangeTermMonths.bind(this);

        this.debouncedCalculateDealAmounts = _.debounce(this.calculateDealAmounts, 250);
    }

    public componentDidMount(): void {
        const { postcodeCategories } = this.props;

        if (!postcodeCategories) {
            this.props.postcodeCategoriesList();
        }

        this.calculateDealAmounts();
    }

    public componentDidUpdate(): void {
        this.debouncedCalculateDealAmounts();
    }

    private calculateDealAmounts(): void {
        const calculateParameters: IDealCalculateParameters = _.omit(this.state, [ 'calculateParametersHash', 'calculatedAmounts' ]);

        const calculateParametersHash: string = objectHash(calculateParameters);

        if (calculateParametersHash !== this.state.calculateParametersHash) {
            const calculatedAmounts: IDealCalculatedAmounts = getDealCalculatedAmounts({
                ...calculateParameters,
                establishmentFeePercentage: this.getEstablishmentFeePercentage(),
                interestMethod: InterestMethodEnum.InterestMethod365,
                interestRate: this.getInterestRate(),
            });

            this.setState({
                calculateParametersHash,
                calculatedAmounts,
            });
        }
    }

    public render(): JSX.Element {
        const { postcodeCategories } = this.props;
        const {
            applicationFeeDollars,
            applicationFeeFormat,
            applicationFeePercentage,
            calculatedAmounts,
            commitmentFee,
            brokerageFeeDollars,
            brokerageFeeFormat,
            brokerageFeePercentage,
            errors,
            establishmentFeeDollars,
            establishmentFeeFormat,
            estimatedOutlays,
            legalFees,
            maximumLvr,
            mortgageType,
            properties,
            requestedPayoutAmount,
            termMonths,
            isThirdParty,
        } = this.state;

        if (!calculatedAmounts || !postcodeCategories) {
            return (
                <Layout className='leads-scenario-calculator'>
                    <Breadcrumb className='breadcrumb'>
                        <Breadcrumb.Item>Home</Breadcrumb.Item>
                        <Breadcrumb.Item>Leads</Breadcrumb.Item>
                        <Breadcrumb.Item>Scenario Calculator</Breadcrumb.Item>
                    </Breadcrumb>
                    <Layout className='content-wrapper'>
                        <Layout.Content className='content'>
                            <Typography.Title level={2}>Scenario Calculator</Typography.Title>
                            <Spin />
                        </Layout.Content>
                    </Layout>
                </Layout>
            );
        }

        const {
            applicationFeeTotal,
            brokerageFeeTotal,
            defaultInterestRate,
            establishmentFeeTotal,
            grossBalanceOnSettlement,
            grossLoanAmount,
            interestPayable,
            lvr,
            monthlyInterestPayments,
            monthlyInterestPaymentsDefault,
            netPrepaidBalanceOnSettlement,
            totalCostsDollars,
            totalCostsPercentage,
            totalCostsPercentagePerAnnum,
        } = calculatedAmounts;

        const property: ICalculatorProperty = properties[0];
        const interestRate: number = this.getInterestRate();
        const calculatedInterestRate: number = this.getCalculatedInterestRate();
        const establishmentFeePercentage: number = this.getEstablishmentFeePercentage();
        const calculatedEstablishmentFeePercentage: number = this.getCalculatedEstablishmentFeePercentage();

        const establishmentFeeMenu: MenuProps = {
            items: [
                {
                    key: 'establishment-fee-format',
                    label: establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickEstablishmentFeeFormat,
                },
            ],
        };

        const establishmentFeeLabel: JSX.Element = (
            <Dropdown menu={establishmentFeeMenu}>
                <a>Establishment Fee <DownOutlined /></a>
            </Dropdown>
        );

        const applicationFeeMenu: MenuProps = {
            items: [
                {
                    key: 'application-fee-format',
                    label: applicationFeeFormat === ApplicationFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickApplicationFeeFormat,
                },
            ],
        };

        const applicationFeeLabel: JSX.Element = (
            <Dropdown menu={applicationFeeMenu}>
                <a>Application Fee <DownOutlined /></a>
            </Dropdown>
        );

        const brokerageFeeMenu: MenuProps = {
            items: [
                {
                    key: 'brokerage-fee-format',
                    label: brokerageFeeFormat === BrokerageFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickBrokerageFeeFormat,
                },
            ],
        };

        const brokerageFeeLabel: JSX.Element = (
            <Dropdown menu={brokerageFeeMenu}>
                <a>Brokerage Fee <DownOutlined /></a>
            </Dropdown>
        );

        const percentageFormatter: Intl.NumberFormat = Intl.NumberFormat('en-AU', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: 'percent',
        });

        const interestRateMinimumLimit: number = mortgageType === MortgageTypeEnum.SecondMortgage ? constants.DEAL_INTEREST_RATE_SECOND_MORTGAGE_MINIMUM : constants.DEAL_INTEREST_RATE_MINIMUM;

        return (
            <Layout className='leads-scenario-calculator'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item>Leads</Breadcrumb.Item>
                    <Breadcrumb.Item>Scenario Calculator</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Scenario Calculator</Typography.Title>
                        <Row>
                            <Col span={12}>
                                <Form.Item label='Mortgage Type' className='mortgage-type'>
                                    <Select onChange={this.onChangeMortgageType} value={mortgageType}>
                                        <Select.Option value={MortgageTypeEnum.FirstMortgage}>First Mortgage</Select.Option>
                                        <Select.Option value={MortgageTypeEnum.SecondMortgage}>Second Mortgage</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label='Loan Term' className='term-months'>
                                    <Select onChange={this.onChangeTermMonths} value={termMonths}>
                                        {_.times(36, (months: number) => (<Select.Option key={months + 1} value={months + 1}>{months + 1} months</Select.Option>))}
                                    </Select>
                                </Form.Item>
                                <Form.Item className='loan-amount' label='Requested Payout Amount' extra={currencyFormatter.format(requestedPayoutAmount)}>
                                    <InputNumber addonBefore='$' onChange={this.onChangeLoanAmount} value={requestedPayoutAmount} />
                                </Form.Item>
                                <Form.Item className='property-value' label='Property Value' extra={currencyFormatter.format(property.estimatedValue)}>
                                    <InputNumber addonBefore='$' onChange={this.onChangePropertyValue} value={property.estimatedValue} />
                                </Form.Item>
                                <Form.Item className='current-debt' label='Current Debt' extra={currencyFormatter.format(property.currentDebt)}>
                                    <InputNumber addonBefore='$' onChange={this.onChangePropertyCurrentDebt} value={property.currentDebt} />
                                </Form.Item>
                                <Form.Item className='postcode' label='Postcode'>
                                    <Input onChange={this.onChangePostcode} value={property.postcode} />
                                </Form.Item>
                                <Form.Item className='zone-type' label='Zoning'>
                                    <Select onChange={this.onChangeZoneType} value={property.zoneType}>
                                        <Select.Option value={ZoneTypeEnum.ResidentialHouse}>Residential - House</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.ResidentialTownhouse}>Residential - Townhouse / Villa</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.ResidentialUnit}>Residential - Unit / Apartment</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.ResidentialLand}>Residential - Land</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.CommercialOffice}>Commercial - Office</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.CommercialRetail}>Commercial - Retail</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.CommercialIndustrial}>Commercial - Industrial</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.CommercialLand}>Commercial - Land</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.RuralResidential}>Rural - Residential</Select.Option>
                                        <Select.Option value={ZoneTypeEnum.RuralLand}>Rural - Land</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label='Broker or Referral Partner' className='is-third-party'>
                                    <Checkbox
                                        checked={isThirdParty}
                                        onChange={this.onChangeIsThirdParty}
                                    />
                                </Form.Item>
                                <Form.Item className='matrix-rate' label='Matrix Rate'>
                                    <span>
                                        {calculatedInterestRate ? percentageFormatter.format(calculatedInterestRate / 100) : '-'}
                                        {calculatedInterestRate && <a onClick={this.onClickUseMatrixInterestRate}> (Use Matrix Rate)</a>}
                                    </span>
                                </Form.Item>
                                <Form.Item
                                    className='interest-rate'
                                    help={errors.interestRate}
                                    label='Interest Rate'
                                    validateStatus={errors.interestRate ? 'error' : (calculatedInterestRate && calculatedInterestRate !== interestRate) && 'warning'}
                                >
                                    <Space>
                                        <InputNumber addonAfter='%' min={interestRateMinimumLimit} onBlur={this.validateInterestRate} onChange={this.onChangeInterestRate} step={0.05} value={interestRate} />
                                        <span>({percentageFormatter.format(defaultInterestRate / 100)} default)</span>
                                    </Space>
                                </Form.Item>
                                <Form.Item className='maximum-lvr' help={errors.maximumLvr} label='Maximum LVR' validateStatus={errors.maximumLvr && 'error'}>
                                    <InputNumber addonAfter='%' max={constants.SCENARIO_LVR_MAXIMUM} onBlur={this.validateMaximumLvr} onChange={this.onChangeMaximumLvr} value={maximumLvr} />
                                </Form.Item>
                                <Form.Item className='commitment-fee' label='Commitment Fee'>
                                    <InputNumber addonBefore='$' onChange={this.onChangeCommitmentFee} value={commitmentFee} />
                                </Form.Item>
                                <Form.Item
                                    className={establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars ? 'establishment-fee-dollars' : 'establishment-fee-percentage'}
                                    help={errors.establishmentFee}
                                    label={establishmentFeeLabel}
                                    validateStatus={errors.establishmentFee ? 'error' : (calculatedEstablishmentFeePercentage && calculatedEstablishmentFeePercentage !== establishmentFeePercentage) && 'warning'}
                                >
                                    {establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' min={constants.DEAL_ESTABLISHMENT_FEE_DOLLARS_MINIMUM} onBlur={this.validateEstablishmentFee} onChange={this.onChangeEstablishmentFeeDollars} value={establishmentFeeDollars} /><span>({percentageFormatter.format(establishmentFeeTotal ? establishmentFeeTotal / grossLoanAmount : 0)})</span></Space>}
                                    {establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' min={constants.DEAL_ESTABLISHMENT_FEE_PERCENTAGE_MINIMUM} onBlur={this.validateEstablishmentFee} onChange={this.onChangeEstablishmentFeePercentage} step={0.1} value={establishmentFeePercentage} /><span>({currencyFormatter.format(establishmentFeeTotal)})</span></Space>}
                                </Form.Item>
                                <Form.Item className={applicationFeeFormat === ApplicationFeeFormatEnum.Dollars ? 'application-fee-dollars' : 'application-fee-percentage'} label={applicationFeeLabel}>
                                    {applicationFeeFormat === ApplicationFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' addonAfter='+ GST' onChange={this.onChangeApplicationFeeDollars} value={applicationFeeDollars} /><span>({percentageFormatter.format(applicationFeeTotal ? applicationFeeTotal / grossLoanAmount : 0)})</span></Space>}
                                    {applicationFeeFormat === ApplicationFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' onChange={this.onChangeApplicationFeePercentage} step={0.1} value={applicationFeePercentage} /><span>({currencyFormatter.format(applicationFeeTotal)} + GST)</span></Space>}
                                </Form.Item>
                                <Form.Item className={brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars ? 'brokerage-fee-dollars' : 'brokerage-fee-percentage'} label={brokerageFeeLabel}>
                                    {brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' addonAfter='+ GST' onChange={this.onChangeBrokerageFeeDollars} value={brokerageFeeDollars} /><span>({percentageFormatter.format(brokerageFeeTotal ? brokerageFeeTotal / grossLoanAmount : 0)})</span></Space>}
                                    {brokerageFeeFormat === BrokerageFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' onChange={this.onChangeBrokerageFeePercentage} step={0.1} value={brokerageFeePercentage} /><span>({currencyFormatter.format(brokerageFeeTotal)} + GST)</span></Space>}
                                </Form.Item>
                                <Form.Item className='legal-fees' help={errors.legalFees} label='Legal Fees' validateStatus={errors.legalFees && 'error'}>
                                    <InputNumber addonBefore='$' addonAfter='+ GST' min={constants.DEAL_LEGAL_FEES_DOLLARS_MINIMUM} onBlur={this.validateLegalFees} onChange={this.onChangeLegalFees} value={legalFees} />
                                </Form.Item>
                                <Form.Item className='approximate-outlays' label='Approximate Outlays'>
                                    <InputNumber addonBefore='$' onChange={this.onChangeEstimatedOutlays} value={estimatedOutlays} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Affix offsetTop={50}>
                                    <Form.Item className='gross-loan-amount' label='Gross Loan Amount'>
                                        {monthlyInterestPayments ? currencyFormatter.format(grossLoanAmount) : '-'}
                                    </Form.Item>
                                    <Form.Item className='lvr' label='LVR'>
                                        {monthlyInterestPayments ? percentageFormatter.format(lvr / 100) : '-'}
                                    </Form.Item>
                                    <Form.Item className='interest-payable' label='Interest Payable'>
                                        {monthlyInterestPayments ? currencyFormatter.format(interestPayable) : '-'}
                                    </Form.Item>
                                    <Form.Item className='monthly-interest-payments' label='Monthly Interest (approx.)'>
                                        {monthlyInterestPayments ? currencyFormatter.format(monthlyInterestPayments) + ` (${currencyFormatter.format(monthlyInterestPaymentsDefault)} default)` : '-'}
                                    </Form.Item>
                                    <Form.Item className='gross-balance-on-settlement' label='Gross Balance on Settlement'>
                                        {monthlyInterestPayments ? currencyFormatter.format(grossBalanceOnSettlement) : '-'}
                                    </Form.Item>
                                    <Form.Item className='net-prepaid-balance-on-settlement' label='Net Balance on Settlement'>
                                        {monthlyInterestPayments ? currencyFormatter.format(netPrepaidBalanceOnSettlement) : '-'}
                                    </Form.Item>
                                    <Form.Item className='total-costs-dollars' label='Total Costs'>
                                        {monthlyInterestPayments ? currencyFormatter.format(totalCostsDollars) + ` (${percentageFormatter.format(totalCostsPercentage / 100)}, ${percentageFormatter.format(totalCostsPercentagePerAnnum / 100)} pa)` : '-'}
                                    </Form.Item>
                                </Affix>
                            </Col>
                        </Row>
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    private getInterestRate(): number {
        if (this.state.dirtyFields.interestRate) {
            return this.state.interestRate;
        }

        return this.getCalculatedInterestRate();
    }

    private getCalculatedInterestRate(): number {
        const { postcodeCategories } = this.props;

        return getDealCalculatedInterestRate({
            ...this.state,
            postcodeCategories,
        });
    }

    private onClickUseMatrixInterestRate(): void {
        this.setState({
            dirtyFields: {
                interestRate: null,
            },
        });
    }

    private getEstablishmentFeePercentage(): number {
        if (this.state.dirtyFields.establishmentFeePercentage) {
            return this.state.establishmentFeePercentage;
        }

        return this.getCalculatedEstablishmentFeePercentage();
    }

    private getCalculatedEstablishmentFeePercentage(): number {
        return getDealCalculatedEstablishmentFeePercentage({
            ...this.state,
        });
    }

    private onClickApplicationFeeFormat(): void {
        const { applicationFeeFormat } = this.state;

        this.setState({
            applicationFeeFormat: applicationFeeFormat === ApplicationFeeFormatEnum.Dollars ? ApplicationFeeFormatEnum.Percentage : ApplicationFeeFormatEnum.Dollars,
        });
    }

    private onClickBrokerageFeeFormat(): void {
        const { brokerageFeeFormat } = this.state;

        this.setState({
            brokerageFeeFormat: brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars ? BrokerageFeeFormatEnum.Percentage : BrokerageFeeFormatEnum.Dollars,
        });
    }

    private onClickEstablishmentFeeFormat(): void {
        const { establishmentFeeFormat } = this.state;

        this.setState({
            establishmentFeeFormat: establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars ? EstablishmentFeeFormatEnum.Percentage : EstablishmentFeeFormatEnum.Dollars,
        });
    }

    private onChangeApplicationFeeDollars(value: number): void {
        this.setState({
            applicationFeeDollars: value,
        });
    }

    private onChangeApplicationFeePercentage(value: number): void {
        this.setState({
            applicationFeePercentage: value,
        });
    }

    private onChangeBrokerageFeeDollars(value: number): void {
        this.setState({
            brokerageFeeDollars: value,
        });
    }

    private onChangeBrokerageFeePercentage(value: number): void {
        this.setState({
            brokerageFeePercentage: value,
        });
    }

    private onChangeCommitmentFee(value: number): void {
        this.setState({
            commitmentFee: value,
        });
    }

    private onChangeEstablishmentFeeDollars(value: number): void {
        this.setState({
            establishmentFeeDollars: value,
        });
    }

    private onChangeEstablishmentFeePercentage(value: number): void {
        const state: IState = {
            ...this.state,
            dirtyFields: {
                ...this.state.dirtyFields,
                establishmentFeePercentage: true,
            },
            establishmentFeePercentage: value,
        };

        this.setState(state);
    }

    private onChangeEstimatedOutlays(value: number): void {
        this.setState({
            estimatedOutlays: value,
        });
    }

    private onChangeIsThirdParty(event: CheckboxChangeEvent): void {
        this.setState({
            isThirdParty: event.target.checked,
        });
    }

    private onChangeLoanAmount(value: number): void {
        this.setState({
            requestedPayoutAmount: value,
        });
    }

    private onChangePropertyValue(value: number): void {
        const { properties } = this.state;

        properties[0].estimatedValue = value;

        this.setState({
            properties,
        });
    }

    private onChangePropertyCurrentDebt(value: number): void {
        const { properties } = this.state;

        properties[0].currentDebt = value;

        this.setState({
            properties,
        });
    }

    private onChangePostcode(event: React.ChangeEvent<HTMLInputElement>): void {
        if (/[^0-9]/.test(event.target.value) || event.target.value.trim().length > 4) {
            return;
        }

        const { properties } = this.state;

        properties[0].postcode = event.target.value;

        this.setState({
            properties,
        });
    }

    private onChangeZoneType(zoneType: ZoneTypeEnum): void {
        const { properties } = this.state;

        properties[0].zoneType = zoneType;

        this.setState({
            properties,
        });
    }

    private onChangeInterestRate(value: number): void {
        const state: IState = {
            ...this.state,
            dirtyFields: {
                ...this.state.dirtyFields,
                interestRate: true,
            },
            interestRate: value,
        };

        this.setState(state);
    }

    private onChangeLegalFees(value: number): void {
        this.setState({
            legalFees: value,
        });
    }

    private onChangeMaximumLvr(value: number): void {
        this.setState({
            maximumLvr: value,
        });
    }

    private validateEstablishmentFee(): boolean {
        const {
            establishmentFeeTotal,
            grossLoanAmount,
        } = this.state.calculatedAmounts;

        return validateDealEstablishmentFee({
            ...this.state,
            establishmentFeeTotal,
            grossLoanAmount,
        }, this.setError);
    }

    private validateInterestRate(): boolean {
        return validateDealInterestRate({
            ...this.state,
        }, this.setError);
    }

    private validateLegalFees(): boolean {
        return validateDealLegalFees({
            ...this.state,
        }, this.setError);
    }

    private validateMaximumLvr(): boolean {
        const { netPrepaidBalanceOnSettlement } = this.state.calculatedAmounts;

        return validateDealMaximumLvr({
            ...this.state,
            netPrepaidBalanceOnSettlement,
        }, this.setError);
    }

    private onChangeMortgageType(value: MortgageTypeEnum): void {
        this.setState({
            mortgageType: value,
        });
    }

    private onChangeTermMonths(value: number): void {
        this.setState({
            termMonths: value,
        });
    }

    private setError(key: keyof IState['errors'], value: string): void {
        this.setState((previousState: IState) => ({
            errors: {
                ...previousState.errors,
                [key]: value,
            },
        }));
    }
}

function mapStateToProps(state: IGlobalState): IPropsSelector {
    return {
        postcodeCategories: dealPropertiesPostcodeCategoriesSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch): IPropsDispatch {
    return {
        postcodeCategoriesList: () => dispatch(dealPropertiesPostcodeCategoriesListAction()),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ScenarioCalculator);
